import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { resolverStackFuenteContrato } from './contratoFuentes';
import { construirContenidoVistaPreviaContratoResuelto } from './plantillaContratoPreview';
import { CONTRATO_A4, obtenerMedidasPaginaContrato } from './contratoPaginaA4';
import { calcularSaltosPaginaContrato, alturaContenidoPagina } from './contratoPaginacionPreview';

/** Escala de captura para PDF (mayor = más nítido, más peso de archivo). */
const PDF_HTML2CANVAS_SCALE = 3;

function formatearFecha(fecha) {
  if (!fecha) return '';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  if (!y || !m || !d) return texto;
  return `${d}/${m}/${y}`;
}

function construirMapaContrato({ empresa, empleado, contrato }) {
  if (contrato?.variables_resueltas && Object.keys(contrato.variables_resueltas).length > 0) {
    return contrato.variables_resueltas;
  }

  return {
    'empresa.nombre_empresa': empresa?.nombre_empresa || empresa?.name || empresa?.nombre || '',
    'empresa.ruc': empresa?.ruc || '',
    'empresa.representante_legal': empresa?.representante_legal || empresa?.representante || '',
    'empresa.direccion': empresa?.direccion || empresa?.address || '',
    'empresa.dni_representante': empresa?.dni_representante || '',
    'empleado.nombre_completo': empleado?.nombre_completo || '',
    'empleado.tipo_documento': empleado?.tipo_documento || '',
    'empleado.numero_documento': empleado?.numero_documento || '',
    'contrato.lugar_firma': contrato?.lugar_firma || '',
    'contrato.fecha_firma': formatearFecha(contrato?.fecha_firma),
  };
}

export function buildHtmlContratoEmpleado({ empresa, empleado, contrato, firmaEmpresaBase64 }) {
  return construirContenidoVistaPreviaContratoResuelto({
    tipo: contrato?.tipo,
    titulo: contrato?.titulo,
    introduccion: contrato?.introduccion,
    articulos: contrato?.articulos,
    mapa: construirMapaContrato({ empresa, empleado, contrato }),
    fuente: contrato?.fuente,
    firmaEmpleadorSrc: firmaEmpresaBase64 || null,
  });
}

function crearMarcaDeAguaPagina(logoSrc) {
  const marca = document.createElement('div');
  marca.setAttribute('aria-hidden', 'true');
  marca.style.cssText =
    'position:absolute;inset:0;z-index:2;pointer-events:none;overflow:hidden;';

  const img = document.createElement('img');
  img.src = logoSrc;
  img.alt = '';
  img.style.cssText =
    'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:58%;max-height:72%;object-fit:contain;opacity:0.1;';
  marca.appendChild(img);

  return marca;
}

async function capturarPaginaContrato({
  htmlContenido,
  offsetY,
  altoVisible,
  medidas,
  fontFamily,
  logoEmpresaBase64,
}) {
  const pageDiv = document.createElement('div');
  pageDiv.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    `width:${medidas.anchoPx}px`,
    `height:${medidas.altoPx}px`,
    `padding:${medidas.paddingTopPx}px ${medidas.paddingRightPx}px ${medidas.paddingBottomPx}px ${medidas.paddingLeftPx}px`,
    'box-sizing:border-box',
    'background:#fff',
    'overflow:hidden',
    `font-family:${fontFamily}`,
    '-webkit-font-smoothing:antialiased',
    'text-rendering:optimizeLegibility',
  ].join(';');

  const clipDiv = document.createElement('div');
  clipDiv.style.cssText = 'position:relative;z-index:1;width:100%;max-width:100%;overflow:hidden;' +
    `height:${altoVisible}px;`;

  const contentDiv = document.createElement('div');
  contentDiv.style.cssText = `margin-top:${-offsetY}px;width:100%;max-width:100%;`;
  contentDiv.innerHTML = htmlContenido;

  clipDiv.appendChild(contentDiv);
  pageDiv.appendChild(clipDiv);

  if (logoEmpresaBase64) {
    pageDiv.style.position = 'fixed';
    pageDiv.appendChild(crearMarcaDeAguaPagina(logoEmpresaBase64));
  }

  document.body.appendChild(pageDiv);

  try {
    const canvas = await html2canvas(pageDiv, {
      scale: PDF_HTML2CANVAS_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: medidas.anchoPx,
      height: medidas.altoPx,
      windowWidth: medidas.anchoPx,
      windowHeight: medidas.altoPx,
      imageTimeout: 0,
      onclone: (clonedDoc) => {
        const pagina = clonedDoc.body.lastElementChild;
        if (!pagina) return;
        pagina.style.setProperty('-webkit-font-smoothing', 'antialiased');
        pagina.style.setProperty('text-rendering', 'optimizeLegibility');
      },
    });

    return canvas.toDataURL('image/png');
  } finally {
    document.body.removeChild(pageDiv);
  }
}

async function htmlPaginadoA4Pdf(htmlContenido, { fuente, logoEmpresaBase64 }) {
  const medidas = obtenerMedidasPaginaContrato();
  const fontFamily = resolverStackFuenteContrato(fuente);
  const pdf = new jsPDF('p', 'mm', 'a4');

  const measureDiv = document.createElement('div');
  measureDiv.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    `width:${medidas.anchoContenidoPx}px`,
    `opacity:0`,
    'pointer-events:none',
    `font-family:${fontFamily}`,
  ].join(';');
  measureDiv.innerHTML = htmlContenido;
  document.body.appendChild(measureDiv);

  try {
    const total = measureDiv.scrollHeight || measureDiv.offsetHeight || 0;
    const saltos = calcularSaltosPaginaContrato(measureDiv, medidas.altoContenidoPx);
    const paginas = saltos.length > 0 ? saltos : [0];

    for (let indice = 0; indice < paginas.length; indice += 1) {
      const offsetY = paginas[indice];
      const altoVisible = alturaContenidoPagina(
        paginas,
        indice,
        medidas.altoContenidoPx,
        total
      );

      const imgData = await capturarPaginaContrato({
        htmlContenido,
        offsetY,
        altoVisible,
        medidas,
        fontFamily,
        logoEmpresaBase64,
      });

      if (indice > 0) {
        pdf.addPage();
      }

      pdf.addImage(
        imgData,
        'PNG',
        0,
        0,
        CONTRATO_A4.anchoMm,
        CONTRATO_A4.altoMm,
        undefined,
        'NONE'
      );
    }

    return pdf;
  } finally {
    document.body.removeChild(measureDiv);
  }
}

export async function exportarContratoEmpleadoPdf({
  empresa,
  empleado,
  contrato,
  firmaEmpresaBase64,
  logoEmpresaBase64,
}) {
  const html = buildHtmlContratoEmpleado({
    empresa,
    empleado,
    contrato,
    firmaEmpresaBase64,
  });

  const pdf = await htmlPaginadoA4Pdf(html, {
    fuente: contrato?.fuente,
    logoEmpresaBase64,
  });

  const nombreEmpleado = (empleado?.nombre_completo || 'empleado').replace(/\s+/g, '_').slice(0, 40);
  const titulo = (contrato?.titulo || 'Contrato')
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_\s-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 40);
  const fecha = new Date().toISOString().split('T')[0];
  pdf.save(`Contrato_${nombreEmpleado}_${titulo}_${fecha}.pdf`);
}
